import { useEffect, useMemo, useState } from "react";
import { Loader2, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TID } from "@/constants/testIds";
import { useT } from "@/i18n";
import { Switch } from "@/components/ui/switch";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useIsMobile } from "@/lib/useIsMobile";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  display_name: "",
  official_name: "",
  swift: "",
  bi_code: "",
  default_transfer_type: "LLG",
  active: true,
};

const FIELD =
  "focus-ring h-11 w-full rounded-xl border border-[#E1E1E6] bg-[#FAFAFA] px-3.5 text-[15px] placeholder:text-[#B0B0B8]";

export default function BankMaster() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const { readOnly } = useAuth();
  const isMobile = useIsMobile();

  const load = async () => {
    try {
      const { data } = await api.get("/banks");
      setItems(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(
      (b) =>
        (b.display_name || "").toLowerCase().includes(s) ||
        (b.official_name || "").toLowerCase().includes(s) ||
        (b.swift || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const startAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const startEdit = (b) => {
    setEditing(b);
    setForm({ ...EMPTY, ...b });
    setOpen(true);
  };

  const save = async () => {
    if (!form.display_name.trim()) {
      toast.error(t("bank.nameReq"));
      return;
    }
    setBusy(true);
    const payload = {
      display_name: form.display_name.trim(),
      official_name: (form.official_name || "").trim(),
      swift: (form.swift || "").trim().toUpperCase(),
      bi_code: (form.bi_code || "").trim(),
      default_transfer_type: form.default_transfer_type || "LLG",
      active: !!form.active,
    };
    try {
      if (editing) {
        const { data } = await api.put(`/banks/${editing.id}`, payload);
        setItems((l) => l.map((x) => (x.id === data.id ? data : x)));
      } else {
        const { data } = await api.post("/banks", payload);
        setItems((l) => [...l, data]);
      }
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (b) => {
    try {
      const { data } = await api.put(`/banks/${b.id}`, {
        display_name: b.display_name,
        official_name: b.official_name,
        swift: b.swift,
        bi_code: b.bi_code,
        default_transfer_type: b.default_transfer_type,
        active: !b.active,
      });
      setItems((l) => l.map((x) => (x.id === data.id ? data : x)));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/banks/${delTarget.id}`);
      setItems((l) => l.filter((x) => x.id !== delTarget.id));
      setDelTarget(null);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-24 text-[14px] text-[#8E8E93]">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid={TID.banks.root}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-[26px] font-semibold tracking-tight sm:text-[30px]">{t("bank.title")}</h1>
          <p className="mt-1 text-[14px] text-[#8E8E93]">
            {t("bank.subtitle")}
          </p>
        </div>
        {!readOnly && (
        <button
          data-testid={TID.banks.add}
          onClick={startAdd}
          className="flex h-10 items-center gap-1.5 self-start rounded-full bg-[#0071E3] px-4 text-[14px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.98] sm:self-auto"
        >
          <Plus className="h-4 w-4" /> {t("bank.add")}
        </button>
        )}
      </div>

      <div className="relative mt-6 max-w-sm">
        <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#B0B0B8]" />
        <input
          data-testid={TID.banks.search}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("bank.search")}
          className="focus-ring h-11 w-full rounded-full border border-[#E1E1E6] bg-white pl-10 pr-4 text-[15px] placeholder:text-[#B0B0B8]"
        />
      </div>

      {isMobile ? (
        <div className="mt-6 space-y-3">
          {filtered.map((b) => (
            <div key={b.id} data-testid={TID.banks.row(b.id)} className={`card-soft px-4 py-3.5 ${b.active ? "" : "opacity-50"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-[15px] font-semibold">
                    {b.display_name}
                    {b.is_bca && (
                      <span className="ml-2 rounded-full bg-[#EEF3FA] px-2 py-0.5 text-[11px] font-medium text-[#0071E3]">BCA</span>
                    )}
                  </p>
                  {b.official_name && <p className="truncate text-[12px] text-[#9A9AA0]">{b.official_name}</p>}
                  <p className="num mt-1 text-[12.5px] text-[#6E6E73]">
                    {b.swift || "—"} · {b.is_bca ? "BCA" : b.default_transfer_type}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {!readOnly && (<>
                  <button
                    data-testid={TID.banks.edit(b.id)}
                    onClick={() => startEdit(b)}
                    className="grid h-8 w-8 place-items-center rounded-full text-[#6E6E73] transition-colors hover:bg-black/[0.05]"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  {!b.is_bca && (
                    <button
                      data-testid={TID.banks.del(b.id)}
                      onClick={() => setDelTarget(b)}
                      className="grid h-8 w-8 place-items-center rounded-full text-[#C8102E] transition-colors hover:bg-[#FDF0F2]"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                  </>)}
                </div>
              </div>
              <div className="mt-2.5 flex items-center justify-between">
                <span className="text-[12.5px] text-[#6E6E73]">{b.active ? t("bank.active") : t("bank.inactive")}</span>
                <Switch data-testid={TID.banks.activeToggle(b.id)} disabled={readOnly} checked={!!b.active} onCheckedChange={() => toggleActive(b)} />
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="card-soft px-5 py-10 text-center text-[14px] text-[#8E8E93]">{t("bank.none")}</p>
          )}
        </div>
      ) : (
      <div className="mt-6 overflow-hidden rounded-2xl border border-[#EDEDF0] bg-white">
        <table className="w-full text-[14px]">
          <thead className="bg-[#F7F7FA] text-[13px] text-[#6E6E73]">
            <tr>
              <th className="px-4 py-3 text-left font-medium">{t("bank.colBank")}</th>
              <th className="hidden px-4 py-3 text-left font-medium sm:table-cell">{t("bank.colSwift")}</th>
              <th className="hidden px-4 py-3 text-left font-medium md:table-cell">{t("bank.colBiCode")}</th>
              <th className="px-4 py-3 text-left font-medium">{t("bank.colType")}</th>
              <th className="px-4 py-3 text-center font-medium">{t("bank.colActive")}</th>
              <th className="px-4 py-3 text-right font-medium">{t("bank.colActions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => (
              <tr key={b.id} data-testid={TID.banks.row(b.id)} className="border-t border-[#F0F0F3]">
                <td className="px-4 py-3">
                  <p className="font-medium">
                    {b.display_name}
                    {b.is_bca && (
                      <span className="ml-2 rounded-full bg-[#EEF3FA] px-2 py-0.5 text-[11px] font-medium text-[#0071E3]">
                        BCA
                      </span>
                    )}
                  </p>
                  {b.official_name && (
                    <p className="text-[12px] text-[#9A9AA0]">{b.official_name}</p>
                  )}
                </td>
                <td className="num hidden px-4 py-3 sm:table-cell">{b.swift || "—"}</td>
                <td className="num hidden px-4 py-3 md:table-cell">{b.bi_code || "—"}</td>
                <td className="px-4 py-3">{b.is_bca ? "BCA" : b.default_transfer_type}</td>
                <td className="px-4 py-3 text-center">
                  <Switch
                    data-testid={TID.banks.activeToggle(b.id)}
                    disabled={readOnly}
                    checked={!!b.active}
                    onCheckedChange={() => toggleActive(b)}
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    {!readOnly && (<>
                    <button
                      data-testid={TID.banks.edit(b.id)}
                      onClick={() => startEdit(b)}
                      className="rounded-lg p-2 text-[#6E6E73] transition-colors hover:bg-black/[0.05]"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    {!b.is_bca && (
                      <button
                        data-testid={TID.banks.del(b.id)}
                        onClick={() => setDelTarget(b)}
                        className="rounded-lg p-2 text-[#C8102E] transition-colors hover:bg-[#FDF0F2]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                    </>)}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-[14px] text-[#8E8E93]">
                  {t("bank.none")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid={TID.banks.dialog}
          className="max-h-[92vh] gap-0 overflow-y-auto rounded-[20px] border border-white/50 bg-white/70 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.18)] ring-1 ring-inset ring-white/50 backdrop-blur-2xl backdrop-saturate-150 sm:max-w-[520px]"
        >
          <DialogHeader className="text-left">
            <DialogTitle className="text-[19px] font-semibold tracking-tight">
              {editing ? editing.display_name : t("bank.new")}
            </DialogTitle>
          </DialogHeader>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <label className="label block">{t("bank.displayName")}</label>
              <input
                data-testid={TID.banks.name}
                value={form.display_name}
                onChange={(e) => set("display_name")(e.target.value)}
                className={FIELD}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label className="label block">{t("bank.officialName")}</label>
              <input
                data-testid={TID.banks.official}
                value={form.official_name}
                onChange={(e) => set("official_name")(e.target.value)}
                className={FIELD}
              />
            </div>
            <div className="space-y-1.5">
              <label className="label block">{t("bank.swiftCode")}</label>
              <input
                data-testid={TID.banks.swift}
                value={form.swift}
                onChange={(e) => set("swift")(e.target.value)}
                placeholder="e.g. BRINIDJA"
                className={`${FIELD} num uppercase`}
              />
            </div>
            <div className="space-y-1.5">
              <label className="label block">{t("bank.biCode")}</label>
              <input
                data-testid={TID.banks.biCode}
                value={form.bi_code}
                onChange={(e) => set("bi_code")(e.target.value)}
                className={`${FIELD} num`}
              />
            </div>
            <div className="space-y-1.5">
              <label className="label block">{t("bank.defaultTransfer")}</label>
              <Select value={form.default_transfer_type} onValueChange={set("default_transfer_type")}>
                <SelectTrigger
                  data-testid={TID.banks.transferType}
                  className="h-11 rounded-xl border-[#E1E1E6] bg-[#FAFAFA] text-[15px]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="LLG">LLG</SelectItem>
                  <SelectItem value="RTG">RTG (RTGS)</SelectItem>
                  <SelectItem value="BCA">BCA (internal)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-[#F7F7FA] px-4 sm:mt-6">
              <span className="text-[14px] font-medium">{t("bank.active")}</span>
              <Switch
                data-testid={TID.banks.active}
                checked={!!form.active}
                onCheckedChange={set("active")}
              />
            </div>
          </div>

          <button
            data-testid={TID.banks.save}
            onClick={save}
            disabled={busy}
            className="mt-6 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#0071E3] text-[15px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.99] disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("bank.save")}
          </button>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!delTarget}
        onOpenChange={(v) => !v && setDelTarget(null)}
        title={t("bank.delTitle")}
        description={delTarget ? t("bank.delDesc", { name: delTarget.display_name }) : ""}
        onConfirm={remove}
        testid={TID.banks.delDialog}
        confirmTestid={TID.banks.delConfirm}
      />
    </div>
  );
}
